// ExistingDelegationPicker — "Use existing delegation" on the leave form (and
// when replacing lost cover on a submitted request).
//
// ⚠️ Prod 2026-10-22: a delegation set up on the Delegations page FIRST, and
// accepted, was invisible to the leave form; Submit stayed disabled and the
// employee made a duplicate. This lists the requester's OWN delegations
// (granted BY them) whose window covers every day of the leave; ones that
// don't are shown with the reason ("ends 10 Nov — your leave runs to 12 Nov")
// instead of silently disappearing. The server re-checks everything.
//
// Four states, never collapsed: loading, error (with retry — a failed lookup
// is never "you have none"), empty, data.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, Plus, UserCheck } from 'lucide-react';
import { DataState } from '@/features/shared/components/ui/DataState';
import { toDisplayDate } from '@/lib/orgFormat';
import type { ExistingDelegationOption } from './handover';

export type ExistingLoadState = 'loading' | 'error' | 'ready';

export interface ExistingDelegationPickerProps {
  state: ExistingLoadState;
  eligible: ExistingDelegationOption[];
  ineligible: ExistingDelegationOption[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onRetry: () => void;
  /** Open the delegation flow to create a new one (omitted → no button). */
  onCreateNew?: () => void;
  startDate: string;
  endDate: string;
  /** Show "Don't attach a delegation" (only where one is optional). */
  allowNone?: boolean;
}

const fmt = (iso: string) => toDisplayDate(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export const ExistingDelegationPicker: React.FC<ExistingDelegationPickerProps> = ({
  state, eligible, ineligible, selectedId, onSelect, onRetry, onCreateNew, startDate, endDate, allowNone,
}) => {
  const { t } = useTranslation(['timeoff']);

  const why = (o: ExistingDelegationOption): string => {
    if (o.reason === 'starts_late') {
      return t('timeoff:existing_starts_late', { first: fmt(o.firstDay), start: fmt(startDate), defaultValue: 'Starts {{first}} — your leave starts {{start}}' });
    }
    if (o.reason === 'ends_early') {
      return t('timeoff:existing_ends_early', { last: fmt(o.lastDay), end: fmt(endDate || startDate), defaultValue: 'Ends {{last}} — your leave runs to {{end}}' });
    }
    return t('timeoff:existing_linked_elsewhere', { defaultValue: 'Already covers another time-off request' });
  };

  const createNew = onCreateNew ? (
    <button
      type="button"
      onClick={onCreateNew}
      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-transparent px-3 py-2 text-xs font-semibold text-foreground hover:bg-accent"
      data-testid="existing-create-new"
    >
      <Plus className="h-3.5 w-3.5" />
      {t('timeoff:existing_create_new', { defaultValue: 'Create new delegation' })}
    </button>
  ) : null;

  if (state === 'loading') {
    return <DataState variant="loading" compact message={t('timeoff:existing_loading', { defaultValue: 'Checking your delegations…' })} />;
  }
  if (state === 'error') {
    return (
      <div className="space-y-2" data-testid="existing-error">
        <DataState
          variant="error"
          compact
          message={t('timeoff:existing_error', { defaultValue: "Couldn't load your delegations. You can retry, or create a new one." })}
          onRetry={onRetry}
        />
        {createNew}
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="existing-delegations">
      <div className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
        {t('timeoff:existing_title', { defaultValue: 'Use an existing delegation' })}
      </div>
      {eligible.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="existing-empty">
          {t('timeoff:existing_none', { defaultValue: 'None of your delegations covers every day of this leave.' })}
        </p>
      ) : (
        <div role="radiogroup" className="space-y-1.5">
          {eligible.map((o) => {
            const on = selectedId === o.delegation.id;
            return (
              <button
                key={o.delegation.id}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onSelect(o.delegation.id)}
                data-testid={`existing-option-${o.delegation.id}`}
                className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2 text-left ${on ? 'border-primary-500 bg-accent' : 'border-border bg-card hover:bg-accent'}`}
              >
                {on ? <CheckCircle2 className="h-4 w-4 shrink-0 text-primary-500" /> : <Circle className="h-4 w-4 shrink-0 text-muted-foreground" />}
                <UserCheck className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-foreground">
                    {o.delegation.delegateName || t('timeoff:coverage_delegated_to', { defaultValue: 'Delegated to' })}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {fmt(o.firstDay)} – {fmt(o.lastDay)}
                  </span>
                </span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${o.accepted ? 'bg-success-soft text-success-strong' : 'bg-warning-soft text-warning-strong'}`}>
                  {o.accepted
                    ? t('timeoff:existing_accepted', { defaultValue: 'Accepted' })
                    : t('timeoff:existing_pending', { defaultValue: 'Awaiting acceptance' })}
                </span>
              </button>
            );
          })}
        </div>
      )}
      {ineligible.length > 0 && (
        <details className="text-xs text-muted-foreground" data-testid="existing-ineligible">
          <summary className="cursor-pointer">
            {t('timeoff:existing_ineligible_count', { count: ineligible.length, defaultValue: "{{count}} other delegation(s) can't cover these dates" })}
          </summary>
          <ul className="mt-1.5 space-y-1 pl-1">
            {ineligible.map((o) => (
              <li key={o.delegation.id} data-testid={`existing-ineligible-${o.delegation.id}`}>
                <span className="font-semibold text-foreground">{o.delegation.delegateName || '—'}</span>
                {' · '}
                {fmt(o.firstDay)} – {fmt(o.lastDay)}
                {' — '}
                <span className="text-warning-strong">{why(o)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {createNew}
        {allowNone && selectedId && (
          <button type="button" onClick={() => onSelect(null)} className="text-xs font-semibold text-muted-foreground hover:underline">
            {t('timeoff:existing_use_none', { defaultValue: "Don't attach a delegation" })}
          </button>
        )}
      </div>
    </div>
  );
};

export default ExistingDelegationPicker;
